import React, { useCallback } from 'react';
import { PlaceholderUFOMigration as Placeholder } from '@atlassian/jira-placeholder/src/index.tsx';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { AnalyticsSource } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import type { IssueDeleteCallbackArgs } from '@atlassian/jira-issue-view-foundation/src/issue-actions/delete-issue/types.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { IssueViewSkeleton } from '@atlassian/jira-skeletons/src/ui/issue/IssueViewSkeleton.tsx';
import type { OnChangeCallback } from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	fireOperationalAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

/* Note: keep it in sync with src/packages/issue/issue-view/src/views/issue-details/issue-preview-panel/entrypoint.tsx */
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssuePreviewPanel = lazy(
	() => import(/* webpackChunkName: "async-issue-preview-panel" */ './index.tsx'),
	{ ssr: false },
);

export type OpenIssuePreviewPanelOptions = {
	issueKey: IssueKey;
	analyticsSource: AnalyticsSource;
	onClose?: () => void;
	onIssueDeleteSuccess?: (arg1: IssueDeleteCallbackArgs) => void;
	onSwitchToModal?: () => void;
	onSwitchToSidebar?: () => void;
	onIssueKeyChange?: (arg1: {
		fromIssueKey: string;
		toIssueKey: string;
		meta?: { location: string } | undefined;
	}) => void;
	onChange?: OnChangeCallback;
	isModeSwitchEnabled: boolean;
	// Analytics context captured from the calling component
	currentAnalyticsContext?: Array<Record<string, unknown>>;
};

export const useIssuePreviewPanel = () => {
	const { open: openPreviewPanel, close: closePreviewPanel } = usePreviewPanelsActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openIssuePreviewPanel = useCallback(
		({
			issueKey,
			analyticsSource,
			onClose,
			onIssueDeleteSuccess,
			onSwitchToModal,
			onSwitchToSidebar,
			onIssueKeyChange,
			onChange,
			isModeSwitchEnabled,
			currentAnalyticsContext,
		}: OpenIssuePreviewPanelOptions) => {
			try {
				openPreviewPanel({
					panel: {
						id: `jira-issue/${issueKey}`,
						type: 'jira-issue',
						content: () => (
							<Placeholder fallback={<IssueViewSkeleton />} name="issue-preview-panel">
								<IssuePreviewPanel
									issueKey={issueKey}
									onClose={onClose || (() => undefined)}
									analyticsSource={analyticsSource}
									onSwitchToModal={onSwitchToModal}
									onSwitchToSidebar={onSwitchToSidebar}
									onIssueDeleteSuccess={onIssueDeleteSuccess}
									onIssueKeyChange={onIssueKeyChange}
									onChange={onChange}
									isModeSwitchEnabled={isModeSwitchEnabled}
									currentAnalyticsContext={currentAnalyticsContext}
								/>
							</Placeholder>
						),
					},
				});

				fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskSuccess', {
					task: 'openIssuePreviewPanel',
					analyticsSource,
				});
			} catch (error) {
				logger.safeErrorWithoutCustomerData(
					'useIssuePreviewPanel.openIssuePreviewPanel',
					`Failed to open issue preview panel for ${issueKey}: ${error instanceof Error ? error.message : 'Unknown error'}`,
				);
				fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskFail', {
					task: 'openIssuePreviewPanel',
					analyticsSource,
					errorMessage: error instanceof Error ? error.message : 'Unknown error',
				});
			}
		},

		[openPreviewPanel, createAnalyticsEvent],
	);

	const closeIssuePreviewPanel = useCallback(() => {
		try {
			closePreviewPanel();
			fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskSuccess', {
				task: 'closeIssuePreviewPanel',
			});
		} catch (error) {
			logger.safeErrorWithoutCustomerData(
				'useIssuePreviewPanel.closeIssuePreviewPanel',
				`Failed to close issue preview panel: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
			fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskFail', {
				task: 'closeIssuePreviewPanel',
				errorMessage: error instanceof Error ? error.message : 'Unknown error',
			});
		}
	}, [closePreviewPanel, createAnalyticsEvent]);

	return {
		openIssuePreviewPanel,
		closePreviewPanel: closeIssuePreviewPanel,
	};
};
